/**
 * Brief Automation Visualizer — friendly illustrated edition.
 *
 * Replaces the previous dark "data-pipeline" panel with bright, illustrated
 * scenes (Facebook / Storyset style): cute character figures, floating
 * phones, paint splashes, document cards, etc.  Each scene blends into
 * the wizard background (no hard square frame) and reacts to what the
 * customer filled in (e.g. their chosen colours bleed into the paint
 * splashes on step 2).
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";

// ---------- Small reusable bits ----------
function FloatingBlob({ color, top, left, size = 200, delay = 0, opacity = 0.4 }) {
  return (
    <div
      className="absolute rounded-full blur-3xl animate-blob-slow pointer-events-none"
      style={{
        background: color,
        top,
        left,
        height: size,
        width: size,
        opacity,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

function HintBubble({ children, top, left, right, delay = 0, color = "#7c3aed" }) {
  return (
    <div
      className="absolute bg-white rounded-2xl px-3 py-1.5 shadow-md text-[11px] font-bold animate-float text-stone-700 whitespace-nowrap"
      style={{
        top, left, right,
        animationDelay: `${delay}s`,
        boxShadow: `0 6px 22px ${color}30`,
        border: `1.5px solid ${color}30`,
      }}
    >
      {children}
    </div>
  );
}

// ---------- Decorative confetti particles (shared) ----------
function ConfettiBurst({ accent, count = 8, top = "20%", left = "50%" }) {
  return (
    <g style={{ transform: `translate(${left}, ${top})` }}>
      {[...Array(count)].map((_, i) => {
        const ang = (i * 360) / count;
        const colors = [accent, "#fbbf24", "#22c55e", "#06b6d4", "#ec4899"];
        return (
          <circle
            key={i}
            cx="0"
            cy="0"
            r="3"
            fill={colors[i % colors.length]}
            className="animate-float"
            style={{
              transform: `rotate(${ang}deg) translate(28px)`,
              animationDelay: `${i * 0.12}s`,
              opacity: 0.85,
            }}
          />
        );
      })}
    </g>
  );
}

// ---------- Scene 1 : "Business idea" ----------
function SceneBusiness({ data, accent }) {
  const charCount = (data.business_what || "").length;
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "מסדרת את הרעיון שלך" : "מסדרים את הרעיון שלך"} emoji="💡">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        {/* Background paper shadow */}
        <ellipse cx="200" cy="270" rx="160" ry="14" fill="#000" opacity="0.06" />
        {/* Wavy lines decoration */}
        <path d="M20 240 Q 60 230 100 240 T 180 240 T 260 240 T 340 240" stroke={accent} strokeWidth="2" fill="none" opacity="0.2" />
        <path d="M40 250 Q 80 240 120 250 T 200 250 T 280 250 T 360 250" stroke="#06b6d4" strokeWidth="2" fill="none" opacity="0.2" />
        {/* Floating notes */}
        <g className="animate-float" style={{ animationDelay: "0.1s" }}>
          <rect x="60" y="60" width="80" height="60" rx="8" fill="#fde68a" transform="rotate(-8 100 90)" />
          <rect x="60" y="60" width="80" height="6" rx="8 8 0 0" fill="#fbbf24" transform="rotate(-8 100 90)" opacity="0.7" />
          <line x1="68" y1="82" x2="120" y2="82" stroke="#92400e" strokeWidth="2" opacity="0.5" transform="rotate(-8 100 90)" />
          <line x1="68" y1="94" x2="110" y2="94" stroke="#92400e" strokeWidth="2" opacity="0.5" transform="rotate(-8 100 90)" />
          <line x1="68" y1="106" x2="115" y2="106" stroke="#92400e" strokeWidth="2" opacity="0.5" transform="rotate(-8 100 90)" />
        </g>
        <g className="animate-float" style={{ animationDelay: "0.6s" }}>
          <rect x="270" y="50" width="70" height="80" rx="8" fill="#bbf7d0" transform="rotate(7 300 90)" />
          <rect x="270" y="50" width="70" height="6" rx="8 8 0 0" fill="#86efac" transform="rotate(7 300 90)" />
          <circle cx="305" cy="80" r="10" fill="#16a34a" opacity="0.6" transform="rotate(7 300 90)" />
          <line x1="278" y1="102" x2="328" y2="102" stroke="#15803d" strokeWidth="2" opacity="0.5" transform="rotate(7 300 90)" />
          <line x1="278" y1="114" x2="318" y2="114" stroke="#15803d" strokeWidth="2" opacity="0.5" transform="rotate(7 300 90)" />
        </g>
        {/* Pink note */}
        <g className="animate-float" style={{ animationDelay: "0.35s" }}>
          <rect x="270" y="155" width="60" height="44" rx="6" fill="#fbcfe8" transform="rotate(-6 300 178)" />
          <line x1="278" y1="170" x2="322" y2="170" stroke="#9d174d" strokeWidth="1.6" opacity="0.45" transform="rotate(-6 300 178)" />
          <line x1="278" y1="180" x2="316" y2="180" stroke="#9d174d" strokeWidth="1.6" opacity="0.45" transform="rotate(-6 300 178)" />
        </g>
        {/* Character — sitting cross-legged with laptop */}
        <g transform="translate(160 130)">
          {/* Lightbulb above with rays */}
          <g className="animate-glow-soft">
            <circle cx="40" cy="-10" r="22" fill="#fef08a" />
            <circle cx="40" cy="-10" r="14" fill="#fde047" />
            <path d="M30 8 L50 8 L48 14 L32 14 Z" fill="#fbbf24" />
            <line x1="36" y1="14" x2="44" y2="14" stroke="#92400e" strokeWidth="2" />
            <g stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round">
              <line x1="14" y1="-10" x2="6" y2="-10" />
              <line x1="66" y1="-10" x2="74" y2="-10" />
              <line x1="22" y1="-32" x2="16" y2="-38" />
              <line x1="58" y1="-32" x2="64" y2="-38" />
              <line x1="40" y1="-40" x2="40" y2="-48" />
              <line x1="6" y1="-26" x2="-2" y2="-30" />
              <line x1="74" y1="-26" x2="82" y2="-30" />
            </g>
          </g>
          {/* Hair */}
          <path d="M22 50 Q22 32 40 30 Q58 32 58 50 Z" fill="#451a03" />
          {/* Face */}
          <circle cx="40" cy="52" r="16" fill="#fde2c4" />
          {/* Cheeks */}
          <circle cx="29" cy="56" r="2.5" fill="#fda4af" opacity="0.6" />
          <circle cx="51" cy="56" r="2.5" fill="#fda4af" opacity="0.6" />
          {/* Smile */}
          <path d="M34 58 Q40 64 46 58" stroke="#451a03" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          <circle cx="35" cy="50" r="1.4" fill="#451a03" />
          <circle cx="45" cy="50" r="1.4" fill="#451a03" />
          {/* Body */}
          <path d="M14 70 Q40 80 66 70 L70 130 L10 130 Z" fill={accent} />
          {/* Logo on shirt */}
          <circle cx="40" cy="92" r="6" fill="white" opacity="0.85" />
          <path d="M37 92 L40 95 L44 89" stroke={accent} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          {/* Arms holding laptop */}
          <ellipse cx="40" cy="115" rx="40" ry="8" fill="#1e293b" />
          <rect x="6" y="105" width="68" height="10" rx="2" fill="#334155" />
          <rect x="10" y="80" width="60" height="28" rx="3" fill="#0f172a" />
          {/* Screen content */}
          <rect x="14" y="84" width="52" height="20" rx="2" fill="#fff" opacity="0.95" />
          <line x1="16" y1="89" x2="50" y2="89" stroke={accent} strokeWidth="1.5" />
          <line x1="16" y1="94" x2="60" y2="94" stroke="#cbd5e1" strokeWidth="1.5" />
          <line x1="16" y1="99" x2="44" y2="99" stroke="#cbd5e1" strokeWidth="1.5" />
        </g>
        {/* Hearts floating */}
        <g className="animate-float" style={{ animationDelay: "0.3s" }}>
          <path d="M340 110 c-3 -6 -12 -3 -12 4 c0 6 12 12 12 12 c0 0 12 -6 12 -12 c0 -7 -9 -10 -12 -4 Z" fill={accent} opacity="0.7" />
        </g>
        <g className="animate-float" style={{ animationDelay: "1.1s" }}>
          <circle cx="50" cy="180" r="8" fill={accent} opacity="0.6" />
        </g>
        {/* Star decorations */}
        <g className="animate-pulse-soft">
          <path d="M30 30 l2 5 l5 1 l-4 3 l1 5 l-4 -3 l-4 3 l1 -5 l-4 -3 l5 -1 Z" fill={accent} opacity="0.7" />
          <path d="M370 200 l2 5 l5 1 l-4 3 l1 5 l-4 -3 l-4 3 l1 -5 l-4 -3 l5 -1 Z" fill="#fbbf24" opacity="0.7" />
        </g>
      </svg>

      <HintBubble top="6%" right="6%" delay={0.2} color={accent}>
        ✨ {data.business_industry || "מנתחים תחום..."}
      </HintBubble>
      <HintBubble top="55%" left="4%" delay={0.7} color={accent}>
        📝 {charCount} תווים נסרקו
      </HintBubble>
    </SceneShell>
  );
}

// ---------- Scene 2 : "Design" ----------
function SceneColors({ data, accent }) {
  const colors = [data.primary_color, data.secondary_color, data.accent_color].filter(Boolean);
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "מערבבת את הצבעים שלך" : "מערבבים את הצבעים שלך"} emoji="🎨">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        <ellipse cx="200" cy="270" rx="150" ry="12" fill="#000" opacity="0.06" />
        {/* Palette */}
        <g transform="translate(120 80)" className="animate-float">
          <ellipse cx="80" cy="60" rx="78" ry="58" fill="#fde68a" />
          <ellipse cx="80" cy="60" rx="78" ry="58" fill="none" stroke="#92400e" strokeWidth="2" opacity="0.3" />
          <circle cx="20" cy="40" r="14" fill={colors[0] || "#7c3aed"} />
          <circle cx="50" cy="20" r="14" fill={colors[1] || "#ec4899"} />
          <circle cx="95" cy="22" r="14" fill={colors[2] || "#facc15"} />
          <circle cx="120" cy="55" r="14" fill={colors[0] || "#22c55e"} opacity="0.85" />
          <circle cx="40" cy="80" r="14" fill={colors[1] || "#06b6d4"} opacity="0.85" />
          <circle cx="80" cy="98" r="9" fill="#fff" stroke="#92400e" strokeWidth="2" opacity="0.5" />
        </g>
        {/* Brush */}
        <g className="animate-brush">
          <rect x="280" y="60" width="14" height="100" rx="3" fill="#7c2d12" transform="rotate(35 287 110)" />
          <path d="M270 50 q-10 5 -10 18 q0 13 10 18 q10 -5 10 -18 q0 -13 -10 -18 Z" fill={colors[0] || accent} transform="rotate(35 287 110)" />
          {/* Drip */}
          <circle cx="246" cy="178" r="6" fill={colors[0] || accent} className="animate-drip" />
        </g>
        {/* Hand */}
        <g transform="translate(200 200)" className="animate-float" style={{ animationDelay: "0.4s" }}>
          <path d="M0 0 q-10 -30 10 -45 q20 -15 40 -5 q15 8 15 25 l5 25 q3 15 -10 25 q-20 12 -45 5 Z" fill="#fde2c4" />
          <rect x="-5" y="-5" width="50" height="10" rx="4" fill={accent} opacity="0.85" />
        </g>
        {/* Splashes */}
        <g className="animate-float" style={{ animationDelay: "0.9s" }}>
          <circle cx="60" cy="60" r="18" fill={colors[0] || "#7c3aed"} opacity="0.6" />
          <circle cx="80" cy="50" r="6" fill={colors[0] || "#7c3aed"} opacity="0.7" />
          <circle cx="45" cy="85" r="5" fill={colors[0] || "#7c3aed"} opacity="0.5" />
        </g>
        <g className="animate-float" style={{ animationDelay: "1.2s" }}>
          <circle cx="350" cy="200" r="14" fill={colors[1] || "#ec4899"} opacity="0.55" />
          <circle cx="332" cy="218" r="6" fill={colors[1] || "#ec4899"} opacity="0.7" />
        </g>
      </svg>

      <HintBubble top="8%" left="6%" delay={0.2} color={colors[0] || accent}>
        🎨 פלטה נשמרה
      </HintBubble>
      <HintBubble top="62%" right="4%" delay={0.7} color={colors[1] || accent}>
        ✏️ {data.font_family || "auto"}
      </HintBubble>
    </SceneShell>
  );
}

// ---------- Scene 3 : "Audience" ----------
function SceneAudience({ data, accent }) {
  const audCount = (data.target_audience || "").split(/[,\n]/).filter(Boolean).length;
  // Three diverse character heads
  const PEOPLE = [
    { hair: "#451a03", skin: "#fde2c4", shirt: accent, x: 80, y: 130 },
    { hair: "#7c2d12", skin: "#f9c8a4", shirt: "#06b6d4", x: 200, y: 100 },
    { hair: "#1e293b", skin: "#e8b88c", shirt: "#f59e0b", x: 320, y: 130 },
  ];
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "מאתרת את הקהל שלך" : "מאתרים את הקהל שלך"} emoji="🎯">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        <ellipse cx="200" cy="270" rx="160" ry="12" fill="#000" opacity="0.06" />
        {/* Connecting circles */}
        <circle cx="200" cy="160" r="100" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="4 6" opacity="0.4" className="animate-spin-very-slow" />
        <circle cx="200" cy="160" r="60" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="4 6" opacity="0.5" className="animate-spin-rev" />
        {/* Center magnet/target */}
        <g transform="translate(180 140)" className="animate-float" style={{ animationDelay: "0.4s" }}>
          <circle cx="20" cy="20" r="20" fill={accent} />
          <circle cx="20" cy="20" r="13" fill="white" />
          <circle cx="20" cy="20" r="6" fill={accent} />
        </g>
        {/* People */}
        {PEOPLE.map((p, i) => (
          <g key={i} transform={`translate(${p.x} ${p.y})`} className="animate-float" style={{ animationDelay: `${i * 0.4}s` }}>
            {/* Hair */}
            <path d={i === 1 ? "M5 22 Q5 0 30 0 Q55 0 55 22 L55 28 Q30 16 5 28 Z" : "M8 22 Q8 4 30 4 Q52 4 52 22 Z"} fill={p.hair} />
            {/* Face */}
            <circle cx="30" cy="22" r="18" fill={p.skin} />
            {/* Smile */}
            <path d="M23 28 Q30 34 37 28" stroke="#451a03" strokeWidth="1.6" fill="none" strokeLinecap="round" />
            <circle cx="24" cy="22" r="1.4" fill="#451a03" />
            <circle cx="36" cy="22" r="1.4" fill="#451a03" />
            {/* Body */}
            <path d="M8 42 Q30 50 52 42 L56 78 L4 78 Z" fill={p.shirt} />
          </g>
        ))}
        {/* Heart in middle */}
        <path d="M198 175 c-4 -7 -14 -3 -14 5 c0 7 14 14 14 14 c0 0 14 -7 14 -14 c0 -8 -10 -12 -14 -5 Z" fill={accent} opacity="0.8" className="animate-pulse-soft" />
      </svg>

      <HintBubble top="8%" right="6%" delay={0.3} color={accent}>
        👥 {audCount || 0} פלחי קהל
      </HintBubble>
      <HintBubble top="60%" left="6%" delay={0.8} color={accent}>
        ✨ קהל אידיאלי זוהה
      </HintBubble>
    </SceneShell>
  );
}

// ---------- Scene 4 : "Channels / Features" ----------
function SceneFeatures({ data, accent }) {
  const features = data.features || [];
  const socials = ["facebook_url", "instagram_url", "tiktok_url"].filter((k) => data[k]);
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "מחברת את הערוצים שלך" : "מחברים את הערוצים שלך"} emoji="🚀">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        <ellipse cx="200" cy="270" rx="160" ry="12" fill="#000" opacity="0.06" />
        {/* Phone in centre */}
        <g transform="translate(160 60)" className="animate-float">
          <rect x="0" y="0" width="80" height="160" rx="14" fill="#1e293b" />
          <rect x="4" y="8" width="72" height="144" rx="8" fill="#f8fafc" />
          {/* Notch */}
          <rect x="32" y="4" width="16" height="3" rx="1.5" fill="#0f172a" />
          {/* Screen widgets */}
          <rect x="10" y="22" width="60" height="26" rx="6" fill={accent} opacity="0.9" />
          <circle cx="22" cy="35" r="6" fill="#fff" />
          <rect x="32" y="30" width="32" height="3" rx="1" fill="#fff" opacity="0.8" />
          <rect x="32" y="36" width="22" height="3" rx="1" fill="#fff" opacity="0.5" />
          <rect x="10" y="55" width="60" height="40" rx="6" fill="#e2e8f0" />
          <circle cx="22" cy="68" r="5" fill="#22c55e" />
          <rect x="32" y="64" width="34" height="3" rx="1" fill="#475569" />
          <rect x="32" y="70" width="24" height="3" rx="1" fill="#94a3b8" />
          <circle cx="22" cy="84" r="5" fill="#06b6d4" />
          <rect x="32" y="80" width="34" height="3" rx="1" fill="#475569" />
          <rect x="32" y="86" width="28" height="3" rx="1" fill="#94a3b8" />
          <rect x="10" y="102" width="60" height="40" rx="6" fill={accent} opacity="0.15" />
        </g>

        {/* Chat bubble — WhatsApp */}
        <g className="animate-float" style={{ animationDelay: "0.2s" }}>
          <rect x="40" y="60" width="80" height="40" rx="14" fill="#22c55e" />
          <path d="M50 96 L50 105 L60 96 Z" fill="#22c55e" />
          <circle cx="60" cy="80" r="8" fill="white" />
          <text x="58" y="84" fontSize="12" fontWeight="bold" fill="#22c55e">W</text>
          <rect x="72" y="74" width="36" height="3" rx="1.5" fill="white" opacity="0.85" />
          <rect x="72" y="82" width="28" height="3" rx="1.5" fill="white" opacity="0.65" />
        </g>
        {/* Email */}
        <g className="animate-float" style={{ animationDelay: "0.5s" }}>
          <rect x="280" y="50" width="80" height="44" rx="10" fill="#06b6d4" />
          <rect x="288" y="60" width="64" height="24" rx="3" fill="white" />
          <path d="M288 60 L320 78 L352 60" stroke="#06b6d4" strokeWidth="2" fill="none" />
        </g>
        {/* Instagram */}
        <g className="animate-float" style={{ animationDelay: "0.8s" }}>
          <rect x="30" y="170" width="50" height="50" rx="14" fill="url(#instaGrad)" />
          <defs>
            <linearGradient id="instaGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#f43f5e" />
              <stop offset="1" stopColor="#a855f7" />
            </linearGradient>
          </defs>
          <rect x="38" y="178" width="34" height="34" rx="8" stroke="white" strokeWidth="2.5" fill="none" />
          <circle cx="55" cy="195" r="7" stroke="white" strokeWidth="2.5" fill="none" />
          <circle cx="65" cy="183" r="2" fill="white" />
        </g>
        {/* TikTok */}
        <g className="animate-float" style={{ animationDelay: "1.1s" }}>
          <rect x="310" y="180" width="50" height="50" rx="14" fill="#0f172a" />
          <text x="320" y="215" fontSize="22" fontWeight="bold" fill="#22d3ee">♪</text>
        </g>
        {/* Connecting lines */}
        <g stroke={accent} strokeWidth="2" strokeDasharray="4 4" opacity="0.5" fill="none">
          <line x1="120" y1="80" x2="170" y2="100" className="animate-pulse-wire" />
          <line x1="280" y1="70" x2="240" y2="100" className="animate-pulse-wire" style={{ animationDelay: "0.3s" }} />
          <line x1="80" y1="195" x2="170" y2="180" className="animate-pulse-wire" style={{ animationDelay: "0.6s" }} />
          <line x1="310" y1="200" x2="240" y2="180" className="animate-pulse-wire" style={{ animationDelay: "0.9s" }} />
        </g>
      </svg>

      <HintBubble top="6%" left="6%" delay={0.2} color={accent}>
        ⚡ {features.length} פיצ'רים
      </HintBubble>
      <HintBubble top="62%" right="4%" delay={0.7} color={accent}>
        📱 {socials.length} רשתות
      </HintBubble>
    </SceneShell>
  );
}

// ---------- Scene 5 : "Files / Cloud" ----------
function SceneFiles({ data, accent }) {
  const files = data.files || [];
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "אוספת את הקבצים שלך" : "אוספים את הקבצים שלך"} emoji="📁">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        <ellipse cx="200" cy="270" rx="170" ry="12" fill="#000" opacity="0.06" />
        {/* Cloud */}
        <g transform="translate(120 70)" className="animate-float">
          <ellipse cx="40" cy="40" rx="38" ry="24" fill="#f0f9ff" />
          <ellipse cx="80" cy="32" rx="42" ry="28" fill="#f0f9ff" />
          <ellipse cx="120" cy="44" rx="36" ry="22" fill="#f0f9ff" />
          <ellipse cx="80" cy="52" rx="72" ry="20" fill="#f0f9ff" />
          {/* Arrow up */}
          <path d="M80 30 L72 42 L78 42 L78 58 L82 58 L82 42 L88 42 Z" fill={accent} />
        </g>
        {/* Folder */}
        <g transform="translate(160 170)">
          <path d="M0 16 L0 12 Q0 6 6 6 L24 6 L30 14 L74 14 Q80 14 80 20 L80 70 Q80 76 74 76 L6 76 Q0 76 0 70 Z" fill={accent} opacity="0.9" />
          <rect x="4" y="22" width="72" height="50" rx="3" fill="#fef3c7" />
        </g>
        {/* Files flying in */}
        <g className="animate-file-in" style={{ animationDelay: "0.15s" }}>
          <rect x="40" y="180" width="36" height="46" rx="5" fill="#fff" stroke={accent} strokeWidth="2" />
          <rect x="45" y="186" width="26" height="3" rx="1.5" fill={accent} opacity="0.7" />
          <rect x="45" y="193" width="20" height="3" rx="1.5" fill="#cbd5e1" />
          <rect x="45" y="200" width="22" height="3" rx="1.5" fill="#cbd5e1" />
          <rect x="45" y="207" width="16" height="3" rx="1.5" fill="#cbd5e1" />
        </g>
        <g className="animate-file-in" style={{ animationDelay: "0.55s" }}>
          <rect x="295" y="170" width="44" height="36" rx="6" fill="#fff" stroke={accent} strokeWidth="2" transform="rotate(8 317 188)" />
          <circle cx="317" cy="188" r="9" fill="#fde68a" transform="rotate(8 317 188)" />
          <path d="M306 200 L320 188 L334 200 Z" fill="#86efac" transform="rotate(8 317 188)" />
        </g>
        {/* Sparkles */}
        {[...Array(6)].map((_, i) => (
          <g key={i} className="animate-float" style={{ animationDelay: `${i * 0.25}s` }}>
            <circle
              cx={50 + i * 55}
              cy={50 + (i % 2) * 30}
              r="3"
              fill={accent}
              opacity="0.6"
            />
          </g>
        ))}
      </svg>

      <HintBubble top="6%" right="6%" delay={0.2} color={accent}>
        📎 {files.length} קבצים
      </HintBubble>
      <HintBubble top="60%" left="6%" delay={0.7} color={accent}>
        ✨ עיבוד אוטומטי
      </HintBubble>
    </SceneShell>
  );
}

// ---------- Scene 6 : "Inspiration / References" ----------
function SceneInspiration({ data, accent }) {
  const refs = (data.reference_urls || []).filter((r) => r.url);
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "מסתכלת על האתרים שאהבת" : "מסתכלים על האתרים שאהבת"} emoji="🌟">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        <ellipse cx="200" cy="270" rx="160" ry="12" fill="#000" opacity="0.06" />
        {/* Magnifying glass */}
        <g className="animate-float">
          <circle cx="160" cy="140" r="56" fill="white" stroke="#0f172a" strokeWidth="6" opacity="0.92" />
          <line x1="200" y1="180" x2="240" y2="220" stroke="#0f172a" strokeWidth="8" strokeLinecap="round" />
          {/* Glass reflection */}
          <ellipse cx="140" cy="120" rx="14" ry="8" fill="white" opacity="0.5" transform="rotate(-30 140 120)" />
          {/* Inside: tiny website mockup */}
          <rect x="120" y="112" width="80" height="56" rx="4" fill="#f1f5f9" />
          <rect x="120" y="112" width="80" height="12" rx="4" fill={accent} />
          <rect x="124" y="128" width="40" height="6" rx="2" fill="#475569" />
          <rect x="124" y="138" width="50" height="4" rx="2" fill="#94a3b8" />
          <rect x="124" y="146" width="40" height="4" rx="2" fill="#94a3b8" />
          <rect x="124" y="154" width="44" height="10" rx="2" fill={accent} opacity="0.85" />
        </g>
        {/* Star bursts */}
        {[...Array(5)].map((_, i) => {
          const filled = i < refs.length;
          return (
            <g key={i} className="animate-float" style={{ animationDelay: `${i * 0.25}s` }}>
              <path
                d={`M${280 + (i % 3) * 35} ${60 + Math.floor(i / 3) * 70} l4 8 l9 1 l-7 6 l2 9 l-8 -5 l-8 5 l2 -9 l-7 -6 l9 -1 Z`}
                fill={filled ? accent : "#cbd5e1"}
                opacity={filled ? "0.9" : "0.4"}
              />
            </g>
          );
        })}
        {/* Sparkles */}
        <g className="animate-pulse-soft">
          <circle cx="60" cy="200" r="4" fill={accent} opacity="0.6" />
          <circle cx="80" cy="220" r="3" fill={accent} opacity="0.5" />
        </g>
      </svg>

      <HintBubble top="6%" right="6%" delay={0.2} color={accent}>
        🔍 {refs.length}/5 השראות
      </HintBubble>
      <HintBubble top="60%" left="6%" delay={0.7} color={accent}>
        ✨ דפוסים עיצוביים זוהו
      </HintBubble>
    </SceneShell>
  );
}

// ---------- Common shell (no hard frame; blends with form) ----------
function SceneShell({ accent, title, emoji, children }) {
  return (
    <div className="relative h-full w-full">
      {/* Soft pastel background blobs — they bleed naturally */}
      <FloatingBlob color={`${accent}33`} top="-10%" right="-10%" size={260} delay={0} opacity={0.55} />
      <FloatingBlob color="#a7f3d055" top="40%" left="-12%" size={220} delay={3} opacity={0.5} />
      <FloatingBlob color="#fce7f3aa" top="50%" left="50%" size={200} delay={6} opacity={0.45} />

      {/* Title */}
      <div className="relative pt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white/85 backdrop-blur px-4 py-1.5 rounded-full shadow-sm border border-white text-stone-700">
          <span className="text-base">{emoji}</span>
          <span className="font-extrabold text-sm">{title}</span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> חי
          </span>
        </div>
      </div>

      {/* Scene illustration */}
      <div className="relative px-4 py-2">{children}</div>
    </div>
  );
}

// ---------- Scene 7 : "Automation needs" — THE MOST IMPORTANT STEP ----------
function SceneAutomations({ data, accent }) {
  const pains = (data.automation_pains || []).length;
  const chars = (data.automation_needs || "").length;
  return (
    <SceneShell accent={accent} title={data.gender === "female" ? "מקשיבה לבעיות שלך 🎧" : "מקשיבים לבעיות שלך 🎧"} emoji="🚀">
      <svg viewBox="0 0 400 300" className="w-full max-w-md mx-auto block">
        <ellipse cx="200" cy="270" rx="160" ry="12" fill="#000" opacity="0.06" />
        {/* Big speech bubble going to a character */}
        <g className="animate-float">
          <path d="M50 80 Q50 50 80 50 L260 50 Q290 50 290 80 L290 130 Q290 160 260 160 L120 160 L90 195 L95 160 L80 160 Q50 160 50 130 Z"
            fill="#fff" stroke={accent} strokeWidth="2.5" />
          {/* Lines inside */}
          <line x1="70" y1="78" x2="265" y2="78" stroke="#e5e7eb" strokeWidth="3" strokeLinecap="round" />
          <line x1="70" y1="95" x2="245" y2="95" stroke="#e5e7eb" strokeWidth="3" strokeLinecap="round" />
          <line x1="70" y1="112" x2="265" y2="112" stroke="#e5e7eb" strokeWidth="3" strokeLinecap="round" />
          <line x1="70" y1="129" x2="220" y2="129" stroke="#e5e7eb" strokeWidth="3" strokeLinecap="round" />
          {/* Highlighted "ideas" tags */}
          <rect x="68" y="138" width="36" height="14" rx="7" fill={accent} opacity="0.85" />
          <rect x="112" y="138" width="46" height="14" rx="7" fill="#22c55e" opacity="0.85" />
          <rect x="166" y="138" width="40" height="14" rx="7" fill="#f59e0b" opacity="0.85" />
        </g>
        {/* Character listening */}
        <g transform="translate(290 165)" className="animate-pulse-soft">
          {/* Hair */}
          <path d="M5 25 Q5 5 30 5 Q55 5 55 25 Z" fill="#1e293b" />
          {/* Face */}
          <circle cx="30" cy="28" r="18" fill="#fde2c4" />
          {/* Big smile */}
          <path d="M22 35 Q30 44 38 35" stroke="#1e293b" strokeWidth="2" fill="none" strokeLinecap="round" />
          {/* Eyes */}
          <circle cx="24" cy="26" r="1.6" fill="#1e293b" />
          <circle cx="36" cy="26" r="1.6" fill="#1e293b" />
          {/* Headphones */}
          <path d="M8 22 Q8 -2 30 -2 Q52 -2 52 22" stroke={accent} strokeWidth="4" fill="none" />
          <rect x="-2" y="20" width="12" height="16" rx="4" fill={accent} />
          <rect x="50" y="20" width="12" height="16" rx="4" fill={accent} />
          {/* Body */}
          <path d="M8 50 Q30 58 52 50 L56 90 L4 90 Z" fill={accent} />
        </g>
        {/* Lightning bolts (energy) */}
        {[...Array(4)].map((_, i) => (
          <g key={i} className="animate-float" style={{ animationDelay: `${i * 0.4}s` }}>
            <path
              d={`M${28 + i * 7} ${210 + (i % 2) * 12} l-4 9 l4 -1 l-2 7 l8 -10 l-5 1 Z`}
              fill="#fbbf24"
              opacity="0.85"
            />
          </g>
        ))}
        {/* Floating idea dots */}
        <g className="animate-pulse-soft">
          <circle cx="350" cy="80" r="6" fill="#22c55e" opacity="0.7" />
          <circle cx="360" cy="130" r="4" fill={accent} opacity="0.6" />
          <circle cx="345" cy="160" r="5" fill="#f59e0b" opacity="0.7" />
        </g>
      </svg>

      <HintBubble top="4%" left="6%" delay={0.2} color={accent}>
        💡 {pains} כאבים זוהו
      </HintBubble>
      <HintBubble top="60%" right="4%" delay={0.7} color={accent}>
        ✍️ {chars} תווים
      </HintBubble>
    </SceneShell>
  );
}

const SCENES = [SceneBusiness, SceneColors, SceneAudience, SceneFeatures, SceneFiles, SceneInspiration, SceneAutomations];

export function BriefAutomationVisualizer({ data, step, accent: accentProp }) {
  const accent = accentProp || data?.primary_color || "#7c3aed";

  // Cross-fade between scenes
  const [prev, setPrev] = useState(step);
  const [fade, setFade] = useState(false);
  const tRef = useRef(null);
  useEffect(() => {
    if (step === prev) return;
    setFade(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      setPrev(step);
      setFade(false);
    }, 280);
    return () => clearTimeout(tRef.current);
  }, [step, prev]);
  const Scene = SCENES[Math.max(0, Math.min(prev, SCENES.length - 1))];

  return (
    <div
      className="relative h-full min-h-[500px] overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #fef9f4 0%, #ffffff 45%, #f0f9ff 100%)",
      }}
      data-testid="brief-automation-visualizer"
    >
      <div className={`absolute inset-0 transition-opacity duration-300 ${fade ? "opacity-0" : "opacity-100"}`}>
        <Scene data={data} accent={accent} />
      </div>

      {/* Step pill row at bottom — coloured dots */}
      <div className="absolute bottom-3 left-0 right-0 z-10">
        <div className="mx-auto max-w-xs flex items-center justify-center gap-1.5">
          {SCENES.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all duration-500"
              style={{
                width: i === step ? 32 : 14,
                background: i <= step ? accent : "rgba(0,0,0,0.12)",
                boxShadow: i === step ? `0 0 8px ${accent}80` : "none",
              }}
            />
          ))}
        </div>
        <div className="mt-1 text-center text-[10px] text-stone-500 font-bold flex items-center justify-center gap-1">
          <Sparkles className="h-3 w-3" />
          שלב {step + 1} מתוך {SCENES.length}
        </div>
      </div>
    </div>
  );
}
